import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  FigureElement,
  FigureHandle,
  MarkSpec,
  MarksHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { GeometryContext } from '../types';
import type { GeometryKey } from '../locales/keys';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import {
  degToRad,
  radToDeg,
  normalizeDeg,
  classifyTransversalPair,
  interiorAngleSum,
  turtleWalkTurns,
  type TransversalAngleId,
  regularPolygonVertices,
} from '@learnforge/demo-math/geometry';
import { ANGLE_PRESETS } from '../data/angle-presets';

// ============================================================================
// Pure Helper Functions (exported and tested)
// ============================================================================

/**
 * Build the around-point model: given ray angles in degrees, compute sectors
 * between consecutive rays, summing to 360.
 */
export function buildAroundPointModel(rayAnglesDeg: number[]): {
  sectors: number[];
} {
  if (rayAnglesDeg.length === 0) {
    return { sectors: [] };
  }

  // Normalize and sort angles
  const normalized = rayAnglesDeg.map((deg) => normalizeDeg(deg)).sort((a, b) => a - b);

  if (normalized.length === 1) {
    return { sectors: [360] };
  }

  const sectors: number[] = [];
  for (let i = 0; i < normalized.length; i++) {
    const curr = normalized[i]!;
    const next = normalized[(i + 1) % normalized.length]!;
    let sector = i === normalized.length - 1 ? 360 - curr + next : next - curr;
    if (sector < 0) {
      sector += 360;
    }
    sectors.push(sector);
  }

  return { sectors };
}

/**
 * Build the parallel-lines model: 8 angles formed by a transversal crossing two parallel lines.
 * Returns angles tagged with TransversalAngleId.
 */
export interface ParallelAngleItem {
  id: TransversalAngleId;
  deg: number;
}

export interface ParallelModel {
  angles: ParallelAngleItem[];
  findPartners(id: TransversalAngleId): ParallelAngleItem[];
}

export function buildParallelModel(transversalDeg: number): ParallelModel {
  const transNorm = normalizeDeg(transversalDeg);

  // Two crossings; each has 4 sectors (0, 1, 2, 3 in CCW order)
  // Crossing 0 (above): angles at sectors 0, 1, 2, 3
  // Crossing 1 (below): angles at sectors 0, 1, 2, 3
  // Interior sectors: crossing 0 → [1, 2]; crossing 1 → [0, 3]

  const angles: ParallelAngleItem[] = [];

  for (let crossing = 0; crossing < 2; crossing++) {
    for (let sector = 0; sector < 4; sector++) {
      // Compute the angle at this position
      // At crossing 0: sectors are 0 (top-left), 1 (bottom-left), 2 (bottom-right), 3 (top-right)
      // At crossing 1: same layout
      // Transversal creates angles: acute and obtuse pairs
      let deg: number;

      // Sectors 0 and 2 are on one side of the transversal (opposite angles = vertical)
      // Sectors 1 and 3 are on the other side
      // For a transversal at angle T crossing parallel lines:
      // The 8 angles consist of 4 equal acute angles and 4 equal obtuse angles (or all 90 if T=90)

      // Simple model: sectors 1 and 3 have angle equal to transversal;
      // sectors 0 and 2 have angle = 180 - transversal
      if (sector === 1 || sector === 3) {
        deg = transNorm;
      } else {
        deg = 180 - transNorm;
      }

      angles.push({
        id: { crossing: crossing as 0 | 1, sector: sector as 0 | 1 | 2 | 3 },
        deg,
      });
    }
  }

  return {
    angles,
    findPartners(id: TransversalAngleId): ParallelAngleItem[] {
      const partners = angles.filter((a) => {
        const relation = classifyTransversalPair(id, a.id);
        return relation !== null && relation !== 'vertical';
      });
      return partners;
    },
  };
}

/**
 * Compute triangulation fan for an n-gon: (n-2) triangles, sum = (n-2)*180°.
 */
export function triangulationFan(n: number): {
  triangles: number;
  angleTotal: number;
} {
  const triangles = n - 2;
  const angleTotal = interiorAngleSum(n);
  return { triangles, angleTotal };
}

/**
 * Build readout rows showing angle relationships in parallel-lines mode.
 *
 * For a selected angle at (crossing, sector):
 * - Corresponding / alternate / vertical angles: lhs = 'α = β', rhs = shared value
 * - Co-interior angles: lhs = 'α + γ', rhs = '180°'
 */
export function buildAngleReadout(
  model: ParallelModel,
  selectedId: TransversalAngleId,
): ReadoutRow[] {
  const rows: ReadoutRow[] = [];
  const selectedAngle = model.angles.find(
    (a) => a.id.crossing === selectedId.crossing && a.id.sector === selectedId.sector,
  );

  if (!selectedAngle) {
    return rows;
  }

  // Iterate all other angles and classify relationship
  const partners = model.angles.filter(
    (a) => !(a.id.crossing === selectedId.crossing && a.id.sector === selectedId.sector),
  );

  for (const partner of partners) {
    const relation = classifyTransversalPair(selectedId, partner.id);

    if (relation === null) {
      continue;
    }

    const id = `angle-${selectedId.crossing}-${selectedId.sector}-to-${partner.id.crossing}-${partner.id.sector}`;

    if (relation === 'corresponding' || relation === 'alternate') {
      rows.push({
        id,
        lhs: 'α = β',
        relation: '=' as const,
        rhs: `${selectedAngle.deg.toFixed(1)}°`,
        emphasis: 'invariant',
      });
    } else if (relation === 'co-interior') {
      rows.push({
        id,
        lhs: 'α + γ',
        relation: '=' as const,
        rhs: '180°',
        emphasis: 'invariant',
      });
    }
    // 'vertical' angles: omit (same crossing, just interior structure)
  }

  return rows;
}

// ============================================================================
// State shape
// ============================================================================

interface AngleChaseState {
  mode: 'around-a-point' | 'parallel-lines' | 'polygon-angles' | 'double-transversal';
  showAngleValues: boolean;
  snapTo5Degrees: boolean;
  anglesOnLine: boolean;
  rayAngles: number[];
  transversalAngle: number;
  transversalAngle2: number;
  polyN: number;
  fanStep: number;
  turtleStep: number;
}

// ============================================================================
// Stage constants
// ============================================================================

const centerPoint: Vec2 = { x: 200, y: 230 };
const rayLength = 150;
const line1Y = 150;
const line2Y = 310;
const lineLeft: Vec2 = { x: 50, y: line1Y };
const lineRight: Vec2 = { x: 750, y: line1Y };
const STAGE_CENTER: Vec2 = { x: 400, y: 230 };
const POLY_RADIUS = 160;

// ============================================================================
// Spec factory
// ============================================================================

function buildSpec(): ExperimentSpec<AngleChaseState, GeometryKey, GeometryContext> {
  // Closure-stashed handles created in setup():
  let figure: FigureHandle;
  let marks: MarksHandle;
  let readout: ReadoutHandle;
  let controllerRef: HarnessController<AngleChaseState>;

  // Transient animation state (NOT in state):
  let turtleRafId: number | null = null;
  let turtleLastTime = 0;

  function cancelTurtleAnimation(): void {
    if (turtleRafId !== null && controllerRef) {
      controllerRef.cancelRaf(turtleRafId);
      turtleRafId = null;
    }
  }

  function startTurtleAnimation(): void {
    if (!controllerRef) return;
    cancelTurtleAnimation();
    turtleLastTime = 0;
    controllerRef.setState((s) => ({ ...s, turtleStep: 0 }));
    const STEP_MS = 500;
    const frame = (time: number): void => {
      if (turtleLastTime === 0) turtleLastTime = time;
      if (time - turtleLastTime >= STEP_MS) {
        turtleLastTime = time;
        let done = false;
        controllerRef.setState((s) => {
          const next = s.turtleStep + 1;
          if (next >= s.polyN) done = true;
          return { ...s, turtleStep: next };
        });
        if (done) {
          turtleRafId = null;
          return;
        }
      }
      turtleRafId = controllerRef.raf(frame);
    };
    turtleRafId = controllerRef.raf(frame);
  }

  const ADVANCED_MODES = ['polygon-angles', 'double-transversal'] as const;

  function effectiveModeFor(state: AngleChaseState, advanced: boolean): AngleChaseState['mode'] {
    return !advanced && (ADVANCED_MODES as readonly string[]).includes(state.mode)
      ? 'around-a-point'
      : state.mode;
  }

  function defaultState(): AngleChaseState {
    return {
      mode: 'around-a-point',
      showAngleValues: true,
      snapTo5Degrees: true,
      anglesOnLine: false,
      rayAngles: [0, 90, 210],
      transversalAngle: 60,
      transversalAngle2: 120,
      polyN: 5,
      fanStep: 0,
      turtleStep: 0,
    };
  }

  function applyPresetLocal(
    state: AngleChaseState,
    presetId: string,
    advanced: boolean,
  ): AngleChaseState {
    const preset = ANGLE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return state;
    if (preset.advancedOnly && !advanced) return state;

    cancelTurtleAnimation();

    const baseNext: AngleChaseState = { ...state, mode: preset.mode };

    if (preset.mode === 'around-a-point') {
      // Mirror the bespoke onPreset exactly: when the preset omits a field, fall
      // back to the bespoke literal default (rayAngles → [0,90,210],
      // anglesOnLine → false), NOT the prior state value.
      const rayAngles = 'rayAngles' in preset ? (preset.rayAngles ?? [0, 90, 210]) : [0, 90, 210];
      const anglesOnLine = 'anglesOnLine' in preset ? (preset.anglesOnLine ?? false) : false;
      return { ...baseNext, rayAngles, anglesOnLine };
    } else if (preset.mode === 'parallel-lines') {
      const transversalAngle = 'transversalAngle' in preset ? (preset.transversalAngle ?? 60) : 60;
      return { ...baseNext, transversalAngle };
    } else if (preset.mode === 'polygon-angles') {
      const polyN =
        presetId === 'pentagon-fan' ? 5 : presetId === 'hexagon-turtle' ? 6 : state.polyN;
      return { ...baseNext, polyN, fanStep: 0, turtleStep: 0 };
    } else if (preset.mode === 'double-transversal') {
      return { ...baseNext, transversalAngle: 60, transversalAngle2: 120 };
    }

    return baseNext;
  }

  function buildParallelFigureElements(
    transAngle: number,
    transPrefix: string,
    trans1X: number,
    snapTo5: boolean,
  ): FigureElement[] {
    const elems: FigureElement[] = [];
    const transRad = degToRad(transAngle);
    const trans1: Vec2 = { x: trans1X, y: line1Y };
    const trans2: Vec2 = {
      x: trans1X + 300 * Math.cos(transRad),
      y: line1Y + 300 * Math.sin(transRad),
    };

    elems.push({
      kind: 'line',
      id: `${transPrefix}-line`,
      through: [`${transPrefix}-p1`, `${transPrefix}-p2`],
      style: { className: 'geo-line-special' },
    });

    elems.push({
      kind: 'point',
      id: `${transPrefix}-p1`,
      at: trans1,
      style: { className: 'geo-point-off-stage' },
    });

    const transEndElem: FigureElement = {
      kind: 'point',
      id: `${transPrefix}-p2`,
      at: trans2,
      style: { className: 'geo-point-draggable' },
      draggable: snapTo5
        ? {
            snap: (p: Vec2) => {
              const angleDeg = radToDeg(Math.atan2(p.y - trans1.y, p.x - trans1.x));
              const snapped = Math.round(angleDeg / 5) * 5;
              const snapRad = degToRad(snapped);
              return {
                x: trans1.x + 300 * Math.cos(snapRad),
                y: trans1.y + 300 * Math.sin(snapRad),
              };
            },
          }
        : {},
    };

    elems.push(transEndElem);
    return elems;
  }

  return {
    labelKey: 'experiment.angle-chase',
    demoId: '2d-geometry',
    advancedKey: 'angle-chase',

    initialState(): AngleChaseState {
      return defaultState();
    },

    applyPreset(state, presetId, ctx): AngleChaseState {
      return applyPresetLocal(state, presetId, ctx.advanced);
    },

    setup(_ctx, controller) {
      controllerRef = controller;
      figure = controller.createFigure();
      marks = controller.createMarks();
      readout = controller.createReadout();
    },

    render(state: AngleChaseState, ctx): void {
      const mode = effectiveModeFor(state, ctx.advanced);

      if (mode === 'around-a-point') {
        renderAroundPointMode(state);
      } else if (mode === 'parallel-lines') {
        renderParallelLinesMode(state);
      } else if (mode === 'polygon-angles') {
        renderPolygonAnglesMode(state);
      } else if (mode === 'double-transversal') {
        renderDoubleTransversalMode(state);
      }

      function renderAroundPointMode(state: AngleChaseState): void {
        const figureElements: FigureElement[] = [];
        const markSpecs: MarkSpec[] = [];

        // Center point
        figureElements.push({
          kind: 'point',
          id: 'center',
          at: centerPoint,
          style: { className: 'geo-point-main' },
        });

        // Sorted ray angles + the sector size between each consecutive pair.
        // The concentric sector arcs crowd together (radius 24 + i·8), so when
        // there are 2+ sectors we tint the single LARGEST one orange to make it
        // stand out from the others.
        const sortedAngles = state.rayAngles.map((deg) => normalizeDeg(deg)).sort((a, b) => a - b);
        const sectorCount = sortedAngles.length;
        const sectorDegs = sortedAngles.map((curr, idx) => {
          const next = sortedAngles[(idx + 1) % sectorCount]!;
          let s = idx === sectorCount - 1 ? 360 - curr + next : next - curr;
          if (s < 0) s += 360;
          return s;
        });
        let largestSectorIdx = -1;
        if (sectorCount >= 2) {
          let maxDeg = -Infinity;
          sectorDegs.forEach((d, idx) => {
            if (d > maxDeg) {
              maxDeg = d;
              largestSectorIdx = idx;
            }
          });
        }

        // Rays and endpoints
        for (let i = 0; i < state.rayAngles.length; i++) {
          const angle = state.rayAngles[i]!;
          const rad = degToRad(angle);
          const endpoint: Vec2 = {
            x: centerPoint.x + rayLength * Math.cos(rad),
            y: centerPoint.y + rayLength * Math.sin(rad),
          };

          figureElements.push({
            kind: 'ray',
            id: `ray-${i}`,
            through: ['center', `ray-end-${i}`],
            style: { className: 'geo-ray' },
          });

          const rayEndElem: FigureElement = {
            kind: 'point',
            id: `ray-end-${i}`,
            at: endpoint,
            style: { className: 'geo-point-draggable' },
            draggable: state.snapTo5Degrees
              ? {
                  snap: (p: Vec2) => {
                    const angleDeg = radToDeg(Math.atan2(p.y - centerPoint.y, p.x - centerPoint.x));
                    const snapped = Math.round(angleDeg / 5) * 5;
                    const snapRad = degToRad(snapped);
                    return {
                      x: centerPoint.x + rayLength * Math.cos(snapRad),
                      y: centerPoint.y + rayLength * Math.sin(snapRad),
                    };
                  },
                }
              : {},
          };

          figureElements.push(rayEndElem);

          // Angle marks for sectors
          if (i < sectorCount) {
            const currAngle = sortedAngles[i]!;
            const nextAngle = sortedAngles[(i + 1) % sectorCount]!;
            const currRad = degToRad(currAngle);
            const nextRad = degToRad(nextAngle);
            const currEnd: Vec2 = {
              x: centerPoint.x + rayLength * Math.cos(currRad),
              y: centerPoint.y + rayLength * Math.sin(currRad),
            };
            const nextEnd: Vec2 = {
              x: centerPoint.x + rayLength * Math.cos(nextRad),
              y: centerPoint.y + rayLength * Math.sin(nextRad),
            };

            const sectorDeg = sectorDegs[i]!;

            const markSpec: MarkSpec & { kind: 'angle' } = {
              kind: 'angle',
              id: `sector-${i}`,
              vertex: centerPoint,
              from: currEnd,
              to: nextEnd,
              radius: 24 + i * 8,
            };

            // The largest of two-or-more crowded sector arcs reads orange.
            if (i === largestSectorIdx) {
              markSpec.color = 'var(--accent)';
            }

            if (state.showAngleValues) {
              markSpec.label = `${Math.round(sectorDeg)}°`;
            }

            markSpecs.push(markSpec);
          }
        }

        // Update views
        figure.update(figureElements);
        marks.update(markSpecs);

        // Readout: show sum = 360 or 180
        const targetSum = state.anglesOnLine ? 180 : 360;
        readout.update([
          {
            id: 'angle-sum',
            lhs: 'Sum',
            relation: '=' as const,
            rhs: `${targetSum}°`,
            emphasis: 'invariant',
          },
        ]);
      }

      function renderParallelLinesMode(state: AngleChaseState): void {
        const figureElements: FigureElement[] = [];
        const markSpecs: MarkSpec[] = [];

        // Parallel lines
        figureElements.push({
          kind: 'line',
          id: 'line1',
          through: ['line1-p1', 'line1-p2'],
          style: { className: 'geo-line' },
        });

        figureElements.push({
          kind: 'line',
          id: 'line2',
          through: ['line2-p1', 'line2-p2'],
          style: { className: 'geo-line' },
        });

        figureElements.push({
          kind: 'point',
          id: 'line1-p1',
          at: lineLeft,
          style: { className: 'geo-point-off-stage' },
        });

        figureElements.push({
          kind: 'point',
          id: 'line1-p2',
          at: lineRight,
          style: { className: 'geo-point-off-stage' },
        });

        figureElements.push({
          kind: 'point',
          id: 'line2-p1',
          at: { x: lineLeft.x, y: line2Y },
          style: { className: 'geo-point-off-stage' },
        });

        figureElements.push({
          kind: 'point',
          id: 'line2-p2',
          at: { x: lineRight.x, y: line2Y },
          style: { className: 'geo-point-off-stage' },
        });

        // Single transversal
        for (const elem of buildParallelFigureElements(
          state.transversalAngle,
          'trans',
          200,
          state.snapTo5Degrees,
        )) {
          figureElements.push(elem);
        }

        figure.update(figureElements);
        marks.update(markSpecs);

        // Build parallel model and show readout for sector 1 (typical teaching angle)
        const model = buildParallelModel(state.transversalAngle);
        const selectedId: TransversalAngleId = { crossing: 0, sector: 1 };
        const rows = buildAngleReadout(model, selectedId);
        readout.update(rows);
      }

      function renderDoubleTransversalMode(state: AngleChaseState): void {
        const figureElements: FigureElement[] = [];
        const markSpecs: MarkSpec[] = [];

        // Parallel lines
        figureElements.push({
          kind: 'line',
          id: 'dt-line1',
          through: ['dt-line1-p1', 'dt-line1-p2'],
          style: { className: 'geo-line' },
        });

        figureElements.push({
          kind: 'line',
          id: 'dt-line2',
          through: ['dt-line2-p1', 'dt-line2-p2'],
          style: { className: 'geo-line' },
        });

        figureElements.push({
          kind: 'point',
          id: 'dt-line1-p1',
          at: { x: 50, y: line1Y },
          style: { className: 'geo-point-off-stage' },
        });

        figureElements.push({
          kind: 'point',
          id: 'dt-line1-p2',
          at: { x: 750, y: line1Y },
          style: { className: 'geo-point-off-stage' },
        });

        figureElements.push({
          kind: 'point',
          id: 'dt-line2-p1',
          at: { x: 50, y: line2Y },
          style: { className: 'geo-point-off-stage' },
        });

        figureElements.push({
          kind: 'point',
          id: 'dt-line2-p2',
          at: { x: 750, y: line2Y },
          style: { className: 'geo-point-off-stage' },
        });

        // First transversal (reuse trans prefix)
        for (const elem of buildParallelFigureElements(
          state.transversalAngle,
          'trans',
          230,
          state.snapTo5Degrees,
        )) {
          figureElements.push(elem);
        }

        // Second transversal (trans2 prefix)
        for (const elem of buildParallelFigureElements(
          state.transversalAngle2,
          'trans2',
          480,
          state.snapTo5Degrees,
        )) {
          figureElements.push(elem);
        }

        figure.update(figureElements);
        marks.update(markSpecs);

        // Show readout for both transversals
        const model1 = buildParallelModel(state.transversalAngle);
        const model2 = buildParallelModel(state.transversalAngle2);
        const rows1 = buildAngleReadout(model1, { crossing: 0, sector: 1 });
        const rows2 = buildAngleReadout(model2, { crossing: 0, sector: 1 }).map((r) => ({
          ...r,
          id: `t2-${r.id}`,
        }));
        readout.update([...rows1, ...rows2]);
      }

      function renderPolygonAnglesMode(state: AngleChaseState): void {
        const figureElements: FigureElement[] = [];
        const markSpecs: MarkSpec[] = [];

        const verts = regularPolygonVertices(state.polyN, POLY_RADIUS, STAGE_CENTER);

        // Register all vertices as points
        for (let i = 0; i < state.polyN; i++) {
          figureElements.push({
            kind: 'point',
            id: `poly-v-${i}`,
            at: verts[i]!,
            style: { className: 'geo-point-main' },
          });
        }

        // Draw polygon edges
        for (let i = 0; i < state.polyN; i++) {
          const next = (i + 1) % state.polyN;
          figureElements.push({
            kind: 'segment',
            id: `poly-edge-${i}`,
            from: `poly-v-${i}`,
            to: `poly-v-${next}`,
            style: { className: 'geo-line' },
          });
        }

        // Triangulation fan from vertex 0
        const { triangles: totalTriangles, angleTotal } = triangulationFan(state.polyN);
        const visibleTriangles = Math.min(state.fanStep, totalTriangles);

        for (let t = 0; t < visibleTriangles; t++) {
          // Triangle t connects vertex 0, vertex t+1, vertex t+2
          const aIdx = 0;
          const bIdx = t + 1;
          const cIdx = t + 2;

          // Draw the two fan diagonals (not the polygon edge)
          if (bIdx !== 1) {
            // Edge 0→bIdx is already a polygon edge only when bIdx=1, otherwise it's a diagonal
            figureElements.push({
              kind: 'segment',
              id: `fan-diag-0-${bIdx}`,
              from: `poly-v-${aIdx}`,
              to: `poly-v-${bIdx}`,
              style: { className: 'geo-line', dashed: true },
            });
          }
          if (cIdx !== state.polyN - 1 || t !== totalTriangles - 1) {
            // Only draw diagonal 0→cIdx if it's not the last polygon edge
            figureElements.push({
              kind: 'segment',
              id: `fan-diag-0-${cIdx}`,
              from: `poly-v-${aIdx}`,
              to: `poly-v-${cIdx}`,
              style: { className: 'geo-line', dashed: true },
            });
          }
        }

        // Turtle walk: show edges walked so far with a highlight
        const cappedTurtle = Math.min(state.turtleStep, state.polyN);
        for (let i = 0; i < cappedTurtle; i++) {
          const next = (i + 1) % state.polyN;
          figureElements.push({
            kind: 'segment',
            id: `turtle-edge-${i}`,
            from: `poly-v-${i}`,
            to: `poly-v-${next}`,
            style: { className: 'geo-line-special' },
          });
        }

        figure.update(figureElements);
        marks.update(markSpecs);

        // Readout: running interior angle total
        const perTriangleAngle = 180;
        const runningInterior = visibleTriangles * perTriangleAngle;

        const readoutRows: ReadoutRow[] = [
          {
            id: 'poly-n',
            lhs: `n`,
            relation: '=' as const,
            rhs: `${state.polyN}`,
            emphasis: 'neutral',
          },
          {
            id: 'poly-triangles',
            lhs: `triangles`,
            relation: '=' as const,
            rhs: `${visibleTriangles} / ${totalTriangles}`,
            emphasis: 'neutral',
          },
          {
            id: 'poly-interior-running',
            lhs: `interior sum`,
            relation: '=' as const,
            rhs:
              visibleTriangles === totalTriangles
                ? `${angleTotal}°`
                : `${runningInterior}° (→ ${angleTotal}°)`,
            emphasis: visibleTriangles === totalTriangles ? 'invariant' : 'neutral',
          },
        ];

        // Turtle exterior angles
        if (cappedTurtle > 0) {
          const turns = turtleWalkTurns(verts);
          const turnsSum = turns
            .slice(0, cappedTurtle)
            .reduce((acc: number, t: number) => acc + t, 0);
          readoutRows.push({
            id: 'turtle-sum',
            lhs: `exterior sum`,
            relation: '=' as const,
            rhs: cappedTurtle === state.polyN ? `360°` : `${Math.round(turnsSum)}° (→ 360°)`,
            emphasis: cappedTurtle === state.polyN ? 'invariant' : 'neutral',
          });
        }

        readout.update(readoutRows);
      }
    },

    panel(state: AngleChaseState, ctx): PanelRow<GeometryKey, AngleChaseState>[] {
      const rows: PanelRow<GeometryKey, AngleChaseState>[] = [];
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      // Mode dropdown
      const modeOptions: { value: string; labelKey: GeometryKey }[] = [
        { value: 'around-a-point', labelKey: 'angle.mode.around-a-point' },
        { value: 'parallel-lines', labelKey: 'angle.mode.parallel-lines' },
      ];
      if (ctx.advanced) {
        modeOptions.push(
          { value: 'polygon-angles', labelKey: 'angle.mode.polygon-angles' },
          { value: 'double-transversal', labelKey: 'angle.mode.double-transversal' },
        );
      }

      rows.push({
        kind: 'dropdown',
        id: 'angle-mode',
        labelKey: 'panel.mode',
        options: modeOptions,
        value: effectiveMode,
        onChange: (v) => {
          const mode = v as AngleChaseState['mode'];
          cancelTurtleAnimation();
          return { ...state, mode };
        },
      });

      // Polygon-angles mode specific rows
      if (effectiveMode === 'polygon-angles') {
        rows.push(
          {
            kind: 'slider',
            id: 'angle-ngon',
            labelKey: 'angle.panel.n-gon-sides',
            min: 3,
            max: 12,
            step: 1,
            value: state.polyN,
            format: (v) => String(v),
            onChange: (v) => {
              cancelTurtleAnimation();
              return { ...state, polyN: v, fanStep: 0, turtleStep: 0 };
            },
          },
          {
            kind: 'slider',
            id: 'angle-fan',
            labelKey: 'Triangles' as GeometryKey,
            min: 0,
            max: state.polyN - 2,
            step: 1,
            value: state.fanStep,
            format: (v) => String(v),
            onChange: (v) => ({ ...state, fanStep: v }),
          },
          {
            kind: 'button',
            labelKey: 'Turtle walk' as GeometryKey,
            onClick: () => {
              startTurtleAnimation();
            },
          },
        );
      }

      // Always-present toggles
      rows.push(
        {
          kind: 'toggle',
          labelKey: 'angle.panel.angles-on-line',
          value: state.anglesOnLine,
          onChange: (v) => ({ ...state, anglesOnLine: v }),
        },
        {
          kind: 'toggle',
          labelKey: 'angle.panel.show-angle-values',
          value: state.showAngleValues,
          onChange: (v) => ({ ...state, showAngleValues: v }),
        },
        {
          kind: 'toggle',
          labelKey: 'angle.panel.snap-to-5',
          value: state.snapTo5Degrees,
          onChange: (v) => ({ ...state, snapTo5Degrees: v }),
        },
      );

      // Advanced toggle
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (state: AngleChaseState, ctx): ActionBarSpec<GeometryKey, AngleChaseState> => {
      const effectiveMode = effectiveModeFor(state, ctx.advanced);

      return {
        onReset: () => {
          cancelTurtleAnimation();
          return {
            ...state,
            rayAngles: [0, 90, 210],
            transversalAngle: 60,
            transversalAngle2: 120,
            polyN: 5,
            fanStep: 0,
            turtleStep: 0,
          };
        },
        onRandomise: () => {
          if (effectiveMode === 'around-a-point') {
            const numRays = 3 + Math.floor(ctx.prng() * 3);
            const rayAngles: number[] = [];
            for (let i = 0; i < numRays; i++) {
              rayAngles.push((ctx.prng() * 360) | 0);
            }
            return { ...state, rayAngles };
          } else if (effectiveMode === 'parallel-lines') {
            return { ...state, transversalAngle: (ctx.prng() * 180) | 0 };
          } else if (effectiveMode === 'double-transversal') {
            return {
              ...state,
              transversalAngle: (ctx.prng() * 180) | 0,
              transversalAngle2: (ctx.prng() * 180) | 0,
            };
          } else if (effectiveMode === 'polygon-angles') {
            cancelTurtleAnimation();
            return {
              ...state,
              polyN: 3 + Math.floor(ctx.prng() * 10),
              fanStep: 0,
              turtleStep: 0,
            };
          }
          return state;
        },
        presets: ANGLE_PRESETS.filter(
          (p) => p.mode === effectiveMode && (ctx.advanced ? true : !p.advancedOnly),
        ).map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id, ctx.advanced),
      };
    },

    onDrag(state: AngleChaseState, pointId: string, at: Vec2): AngleChaseState {
      const effectiveMode = effectiveModeFor(state, controllerRef.advanced);

      // Around-a-point: ray-end-N
      const rayMatch = pointId.match(/^ray-end-(\d+)$/);
      if (rayMatch) {
        const idx = parseInt(rayMatch[1]!, 10);
        const angleDeg = radToDeg(Math.atan2(at.y - centerPoint.y, at.x - centerPoint.x));
        const rayAngles = state.rayAngles.slice();
        rayAngles[idx] = normalizeDeg(angleDeg);
        return { ...state, rayAngles };
      }

      // Parallel-lines transversal 1 (also used in double-transversal)
      if (pointId === 'trans-p2') {
        const trans1X = effectiveMode === 'double-transversal' ? 230 : 200;
        const trans1: Vec2 = { x: trans1X, y: line1Y };
        const angleDeg = radToDeg(Math.atan2(at.y - trans1.y, at.x - trans1.x));
        return { ...state, transversalAngle: normalizeDeg(angleDeg) };
      }

      // Double-transversal: second transversal
      if (pointId === 'trans2-p2') {
        const trans2Start: Vec2 = { x: 480, y: line1Y };
        const angleDeg = radToDeg(Math.atan2(at.y - trans2Start.y, at.x - trans2Start.x));
        return { ...state, transversalAngle2: normalizeDeg(angleDeg) };
      }

      return state;
    },

    applyShotState(state: AngleChaseState, shotId: string): AngleChaseState {
      if (shotId === 'angle-chase') {
        return applyPresetLocal(state, 'z-angles', true);
      }
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());
